// pages/Users/users.js - Complete with City Filter + FaUserPlus Icon + Custom Dropdown Arrow + Fixed Actions Cell

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaUserPlus, FaSearch, FaUserCheck, FaUserTimes,
  FaTrash, FaCheckDouble, FaEllipsisV, FaEdit,
  FaStore, FaBuilding, FaShieldAlt, FaUsers, FaUser, FaMapMarkerAlt
} from 'react-icons/fa';
import { RiDeleteBin5Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './users.css';

// Last loaded users of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const USERS_CACHE_PREFIX = 'users_cache_';
const USERS_CITIES_PREFIX = 'users_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getShopIdFromLocation = (pathname, search) => {
  const match = pathname.match(/\/shop\/(\d+)/);
  if (match) return parseInt(match[1]);
  const shopId = new URLSearchParams(search).get('shop_id');
  return shopId ? parseInt(shopId) : null;
};

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ The shop is known from the very first render (taken straight from the URL, not set
  // later by an effect), so the list never shows the users of ALL shops for a moment.
  const currentShopId = useMemo(
    () => getShopIdFromLocation(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const key = currentShopId || 'all';
    const cachedUsers = readJson(`${USERS_CACHE_PREFIX}${key}`);
    const cachedCities = currentShopId ? readJson(`${USERS_CITIES_PREFIX}${currentShopId}`) : null;
    initialRef.current = {
      users: Array.isArray(cachedUsers) ? cachedUsers : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [usersList, setUsersList] = useState(initialRef.current.users || []);
  const [allUsersList, setAllUsersList] = useState(initialRef.current.users || []);
  const [shops, setShops] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // True only while there is nothing to show yet (so "No users found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.users);

  // ✅ City Dropdown ke liye state
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [selectedCity, setSelectedCity] = useState('');

  const selectedCityRef = useRef('');
  const allRequestRef = useRef(0);
  const cityRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const prevShopKeyRef = useRef(currentShopId || 'all');

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Users, shops and cities load together instead of one after another.
  useEffect(() => {
    const key = currentShopId || 'all';

    if (prevShopKeyRef.current !== key) {
      prevShopKeyRef.current = key;
      const cachedUsers = readJson(`${USERS_CACHE_PREFIX}${key}`);
      const cachedCities = currentShopId ? readJson(`${USERS_CITIES_PREFIX}${currentShopId}`) : null;
      if (Array.isArray(cachedUsers)) {
        setUsersList(cachedUsers);
        setAllUsersList(cachedUsers);
        setIsLoading(false);
      } else {
        setUsersList([]);
        setAllUsersList([]);
        setIsLoading(true);
      }
      setCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedCity('');
      selectedCityRef.current = '';
      setSelectedUsers([]);
    }

    if (localStorage.getItem('access_token')) {
      fetchShops();
      fetchUsers();
    } else {
      setIsLoading(false);
    }

    if (currentShopId) {
      fetchShopCities(currentShopId);
    } else {
      setCities([]);
      setSelectedCity('');
      selectedCityRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const fetchShops = async () => {
    try {
      const response = await axiosInstance.get('/user/shops/');
      console.log('✅ Shops data:', response.data);
      setShops(response.data);
    } catch (err) {
      console.error('Error fetching shops:', err);
    }
  };

  const fetchShopCities = async (shopId) => {
    if (!shopId) return;
    const requestId = ++citiesRequestRef.current;
    try {
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      if (requestId !== citiesRequestRef.current) return;
      console.log('✅ Shop cities response:', response.data);

      let citiesData = [];

      if (response.data) {
        if (Array.isArray(response.data.cities)) {
          citiesData = response.data.cities;
        } else if (Array.isArray(response.data)) {
          citiesData = response.data;
        } else if (typeof response.data.cities === 'string') {
          try {
            citiesData = JSON.parse(response.data.cities);
          } catch {
            citiesData = response.data.cities.split(',').map(c => c.trim()).filter(c => c);
          }
        }
      }

      setCities(citiesData);
      writeJson(`${USERS_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (err) {
      if (requestId !== citiesRequestRef.current) return;
      console.error('Error fetching shop cities:', err);
    }
  };

  // ✅ Fetch users (runs in the background: only the latest request of each kind updates the screen).
  // cityFilter = null  -> all users of the shop (master list, also saved for next time)
  // cityFilter = 'xyz' -> users of that city (backend filter)
  const fetchUsers = async (cityFilter = null) => {
    const isCity = !!(cityFilter && cityFilter !== '' && cityFilter !== 'all');
    const requestRef = isCity ? cityRequestRef : allRequestRef;
    const requestId = ++requestRef.current;
    const shopKey = currentShopId || 'all';

    try {
      let url = '/user/users/';
      const params = new URLSearchParams();

      if (currentShopId) {
        params.append('shop_id', currentShopId);
      }

      if (isCity) {
        params.append('city', cityFilter);
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      console.log('🔄 Fetching users:', url);
      const response = await axiosInstance.get(url);
      if (requestId !== requestRef.current) return;
      console.log('✅ Users response:', response.data.length, 'users');

      const filteredUsers = response.data.filter(user => user.type !== 'SUPER_ADMIN');

      if (!isCity) {
        // ✅ Master list (all cities) - used to calculate accurate city counts
        setAllUsersList(filteredUsers);
        writeJson(`${USERS_CACHE_PREFIX}${shopKey}`, filteredUsers);
        const activeCity = selectedCityRef.current;
        setUsersList(
          activeCity ? filteredUsers.filter(user => user.selected_city === activeCity) : filteredUsers
        );
      } else if (cityFilter === selectedCityRef.current) {
        setUsersList(filteredUsers);
      }

    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Error fetching users:', err);
      if (err.response?.status === 401) {
        Swal.fire({
          icon: 'error',
          title: 'Session Expired',
          text: 'Please login again.',
          confirmButtonColor: '#004aad'
        }).then(() => {
          navigate('/signin');
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load users',
          confirmButtonColor: '#004aad'
        });
      }
    } finally {
      if (!isCity && requestId === requestRef.current) setIsLoading(false);
    }
  };

  // City change: the list is filtered instantly from the loaded users,
  // and refreshed from the server in the background.
  const applyCity = (newCity) => {
    setSelectedCity(newCity);
    selectedCityRef.current = newCity;
    setSelectedUsers([]);
    setUsersList(newCity ? allUsersList.filter(user => user.selected_city === newCity) : allUsersList);
    if (newCity) fetchUsers(newCity);
  };

  const handleCityChange = (e) => {
    applyCity(e.target.value);
  };

  const filteredUsers = usersList.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.first_Name + ' ' + user.last_Name).toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.company_name && user.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
    setShowActionDropdown(false);
  };

  // Deleted users disappear right away (and from the saved copy too)
  const removeUsersLocally = (ids) => {
    const remove = (list) => list.filter(u => !ids.includes(u.id));
    setUsersList(prev => remove(prev));
    setAllUsersList(prev => {
      const next = remove(prev);
      writeJson(`${USERS_CACHE_PREFIX}${currentShopId || 'all'}`, next);
      return next;
    });
  };

  const handleDeleteUser = async (user) => {
    const result = await Swal.fire({
      title: 'Delete User?',
      html: `Are you sure you want to delete <strong>${user.first_Name} ${user.last_Name}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/user/users/${user.id}/`);
        removeUsersLocally([user.id]);
        setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
        fetchUsers(null);

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `${user.first_Name} ${user.last_Name} has been deleted successfully.`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error deleting user:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete user. Please try again.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Selection',
        text: 'Please select users to delete',
        timer: 2000,
        showConfirmButton: true,
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Selected Users?',
      text: `Are you sure you want to delete ${selectedUsers.length} selected user(s)? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete All',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const deletedIds = [];
        for (const user of selectedUsers) {
          await axiosInstance.delete(`/user/users/${user.id}/`);
          deletedIds.push(user.id);
        }
        const deletedCount = selectedUsers.length;
        removeUsersLocally(deletedIds);
        setSelectedUsers([]);
        setShowActionDropdown(false);
        fetchUsers(null);

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `${deletedCount} user(s) deleted successfully.`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error deleting users:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete users. Please try again.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...filteredUsers]);
    }
    setShowActionDropdown(false);
  };

  const handleAddUserClick = () => {
    if (currentShopId) {
      navigate(`/shop/${currentShopId}/add-user`);
    } else {
      navigate('/add-user');
    }
  };

  const handleEditUser = (user) => {
    if (currentShopId) {
      navigate(`/shop/${currentShopId}/edit-user/${user.id}`);
    } else {
      navigate(`/edit-user/${user.id}`);
    }
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown]);

  const getUserTypeBadgeClass = (type) => {
    if (!type) return 'user-type-badge default';
    return `user-type-badge ${type.toLowerCase()}`;
  };

  const currentShop = shops.find(s => s.shop_id === currentShopId);

  // ✅ Calculate total users for "All Cities" (using master list)
  const allCitiesCount = allUsersList.length;

  // ✅ Helper to calculate count for a specific city (using master list)
  const getCityCount = (cityName) => {
    return allUsersList.filter(user => user.selected_city === cityName).length;
  };

  return (
    <div className="users-container">
      <div className="users-header-section">
        <div className="users-header-left">
          <FaUserPlus className="users-header-icon" />
          <h2 className="users-header-title">Users Management</h2>
          {/* ✅ Dynamic City Badge with Count */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedCity || 'All Cities'} ({selectedCity ? getCityCount(selectedCity) : allCitiesCount})
            </span>
          </span>
        </div>

        <div className="users-header-right">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedCity}
              onChange={handleCityChange}
              disabled={cities.length === 0}
            >
              {/* ✅ Dynamic "All Cities" option showing total count */}
              <option value="">
                All Cities ({allCitiesCount})
              </option>

              {/* ✅ Dynamic city options showing their respective counts */}
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({getCityCount(city)})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}</span>
          </button>
          <button className="add-user-btn" onClick={handleAddUserClick}>
            <FaUserPlus />
            <span>ADD USER</span>
          </button>
        </div>
      </div>

      <div className="users-content">
        {selectedUsers.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedUsers.length} user(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedUsers.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={() => {
                setSelectedUsers([]);
                setShowActionDropdown(false);
              }}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="users-table-container">
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  {!currentShopId && <th>Shop</th>}
                  <th>City</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so no wrong / empty list ever flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan={currentShopId ? 8 : 9} style={{ height: '200px' }}></td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className={selectedUsers.some(u => u.id === user.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.some(u => u.id === user.id)}
                          onChange={() => toggleUserSelection(user)}
                        />
                      </td>
                      <td>
                        <div className="user-name-cell">
                          {user.first_Name} {user.last_Name}
                        </div>
                      </td>
                      <td>
                        {user.email}
                      </td>
                      <td>
                        {user.phone_Number || 'N/A'}
                      </td>
                      {!currentShopId && (
                        <td>
                          <span className="shop-badge">
                            {user.shop_name || 'No Shop'}
                          </span>
                        </td>
                      )}
                      <td>
                        <span className="user-city-cell">
                          {user.selected_city || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={getUserTypeBadgeClass(user.type)}>
                          {user.type}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                          <span>{user.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      </td>
                      {/* ✅ Actions cell - wrapper ke andar buttons */}
                      <td className="actions-cell">
                        <div className="actions-wrapper">
                          <button
                            className="edit-address-btn"
                            onClick={() => handleEditUser(user)}
                            title="Edit User"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="delete-user-btn"
                            onClick={() => handleDeleteUser(user)}
                            title="Delete User"
                          >
                            <RiDeleteBin5Fill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredUsers.length === 0 && (
            <div className="no-users">
              <FaBuilding className="no-data-icon" />
              <p>
                {selectedCity
                  ? `No users found in ${selectedCity}.`
                  : 'No users found.'}
              </p>
              {selectedCity && (
                <button
                  className="add-first-btn"
                  onClick={() => applyCity('')}
                >
                  Clear City Filter
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;